import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import {
  Target, Clock, Sparkles, Building, UserCircle, X,
  Check, XCircle, ArrowRight, ChevronDown, ChevronUp,
  Lightbulb, ShieldCheck, ScrollText, RefreshCw, Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const ICON_MAP = {
  target: Target, clock: Clock, sparkles: Sparkles,
  building: Building, 'user-circle': UserCircle, lightbulb: Lightbulb,
};

const SEVERITY_STYLES = {
  action: { bg: 'rgba(217,119,6,0.16)', border: '#F59E0B', text: '#FCD9A0', badge: '#D97706', badgeText: '#FFF' },
  warning: { bg: 'rgba(245,158,11,0.16)', border: '#FBBF24', text: '#FCE3B0', badge: '#F59E0B', badgeText: '#FFF' },
  info: { bg: 'rgba(59,130,246,0.16)', border: '#60A5FA', text: '#BFD4FF', badge: '#3B82F6', badgeText: '#FFF' },
};

const SEVERITY_LABELS = {
  nl: { action: 'Actie vereist', warning: 'Aandacht', info: 'Suggestie' },
  en: { action: 'Action required', warning: 'Attention', info: 'Suggestion' },
};

const TYPE_LABELS = {
  nl: {
    approval_requested: 'Goedkeuring gevraagd',
    approval_response: 'Goedkeuring beantwoord',
    action_executed: 'Actie uitgevoerd',
    insight_generated: 'Inzicht gegenereerd',
    insight_dismissed: 'Inzicht weggedrukt',
  },
  en: {
    approval_requested: 'Approval requested',
    approval_response: 'Approval responded',
    action_executed: 'Action executed',
    insight_generated: 'Insight generated',
    insight_dismissed: 'Insight dismissed',
  },
};

const EmmaProactive = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = lang === 'nl' ? 'nl' : 'en';

  const [insights, setInsights] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('insights');
  const [timelineExpanded, setTimelineExpanded] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [insRes, appRes, tlRes] = await Promise.all([
        axios.get(`${API}/emma-proactive/insights?language=${lang}`, { headers }),
        axios.get(`${API}/emma-proactive/approvals`, { headers }),
        axios.get(`${API}/emma-proactive/timeline?limit=30`, { headers }),
      ]);
      setInsights(insRes.data.insights || []);
      setApprovals(appRes.data.approvals || []);
      setTimeline(tlRes.data.events || []);
    } catch (e) {
      console.error('Proactive fetch error:', e);
    }
    setLoading(false);
  }, [token, lang]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const dismissInsight = async (id) => {
    setInsights(prev => (Array.isArray(prev) ? prev : []).filter(i => i.id !== id));
    try {
      await axios.post(`${API}/emma-proactive/insights/${id}/dismiss`, {}, { headers });
    } catch (e) { console.error(e); }
  };

  const respondApproval = async (id, decision) => {
    setApprovals(prev => (Array.isArray(prev) ? prev : []).map(a => a.id === id ? { ...a, status: decision } : a));
    try {
      await axios.post(`${API}/emma-proactive/approvals/${id}/respond`, { decision }, { headers });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const formatTimeAgo = (isoStr) => {
    if (!isoStr) return '';
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return lang === 'nl' ? 'Zojuist' : 'Just now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  };

  const visibleTimeline = timelineExpanded ? timeline : timeline.slice(0, 5);

  return (
    <div className="emma-proactive-page" data-testid="emma-proactive-page">
      {/* Header */}
      <div className="ep-header" data-testid="ep-header">
        <div className="ep-header-left">
          <div className="ep-header-icon">
            <Zap size={24} />
          </div>
          <div>
            <h1 className="ep-title">
              {lang === 'nl' ? 'Proactieve Emma' : 'Proactive Emma'}
            </h1>
            <p className="ep-subtitle">
              {lang === 'nl'
                ? 'Emma analyseert je bedrijf en stelt acties voor'
                : 'Emma analyzes your business and suggests actions'}
            </p>
          </div>
        </div>
        <button className="ep-refresh-btn" onClick={fetchAll} data-testid="ep-refresh-btn">
          <RefreshCw size={16} className={loading ? 'ep-spinning' : ''} />
          {lang === 'nl' ? 'Vernieuw' : 'Refresh'}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="ep-tabs" data-testid="ep-tabs">
        <button
          className={`ep-tab ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
          data-testid="ep-tab-insights"
        >
          <Lightbulb size={16} />
          {lang === 'nl' ? 'Inzichten' : 'Insights'}
          {insights.length > 0 && <span className="ep-tab-badge">{insights.length}</span>}
        </button>
        <button
          className={`ep-tab ${activeTab === 'approvals' ? 'active' : ''}`}
          onClick={() => setActiveTab('approvals')}
          data-testid="ep-tab-approvals"
        >
          <ShieldCheck size={16} />
          {lang === 'nl' ? 'Goedkeuringen' : 'Approvals'}
          {approvals.length > 0 && <span className="ep-tab-badge">{approvals.length}</span>}
        </button>
        <button
          className={`ep-tab ${activeTab === 'timeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('timeline')}
          data-testid="ep-tab-timeline"
        >
          <ScrollText size={16} />
          {lang === 'nl' ? 'Tijdlijn' : 'Timeline'}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="ep-loading" data-testid="ep-loading">
          <div className="loader" />
        </div>
      ) : (
        <>
          {/* ── INSIGHTS TAB ── */}
          {activeTab === 'insights' && (
            <div className="ep-section" data-testid="ep-insights-section">
              {insights.length === 0 ? (
                <div className="ep-empty" data-testid="ep-no-insights">
                  <Sparkles size={40} style={{ color: 'var(--text-muted)' }} />
                  <p>{lang === 'nl'
                    ? 'Geen inzichten op dit moment — Emma houdt je bedrijf in de gaten!'
                    : 'No insights right now — Emma is keeping an eye on your business!'}</p>
                </div>
              ) : (
                <div className="ep-cards-grid">
                  {(Array.isArray(insights) ? insights : []).map((insight) => {
                    const Icon = ICON_MAP[insight.icon] || Lightbulb;
                    const sev = SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.info;
                    return (
                      <div
                        key={insight.id}
                        className="ep-insight-card"
                        style={{ borderLeftColor: sev.border }}
                        data-testid={`ep-insight-${insight.id}`}
                      >
                        <div className="ep-card-top">
                          <div className="ep-card-icon" style={{ background: sev.bg }}>
                            <Icon size={18} style={{ color: sev.border }} />
                          </div>
                          <span className="ep-severity-badge" style={{ background: sev.badge, color: sev.badgeText }}>
                            {SEVERITY_LABELS[t][insight.severity]}
                          </span>
                          <button
                            className="ep-dismiss-btn"
                            onClick={() => dismissInsight(insight.id)}
                            data-testid={`ep-dismiss-${insight.id}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <p className="ep-card-message">{insight.message}</p>
                        {insight.action_route && (
                          <button
                            className="ep-action-btn"
                            onClick={() => navigate(insight.action_route)}
                            data-testid={`ep-action-${insight.id}`}
                          >
                            {insight.suggested_action}
                            <ArrowRight size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── APPROVALS TAB ── */}
          {activeTab === 'approvals' && (
            <div className="ep-section" data-testid="ep-approvals-section">
              {approvals.length === 0 ? (
                <div className="ep-empty" data-testid="ep-no-approvals">
                  <ShieldCheck size={40} style={{ color: 'var(--text-muted)' }} />
                  <p>{lang === 'nl'
                    ? 'Geen openstaande goedkeuringen — Emma wacht op jouw instructies.'
                    : 'No pending approvals — Emma is waiting for your instructions.'}</p>
                </div>
              ) : (
                <div className="ep-cards-grid">
                  {(Array.isArray(approvals) ? approvals : []).map((approval) => (
                    <div key={approval.id} className="ep-approval-card" data-testid={`ep-approval-${approval.id}`}>
                      <div className="ep-card-top">
                        <div className="ep-card-icon" style={{ background: '#16202C' }}>
                          <ShieldCheck size={18} style={{ color: '#7C3AED' }} />
                        </div>
                        <span className="ep-approval-type">{approval.action_type}</span>
                        <span className="ep-approval-time">{formatTimeAgo(approval.created_at)}</span>
                      </div>
                      <p className="ep-card-message">
                        {lang === 'nl' ? approval.description_nl : approval.description_en}
                      </p>
                      {approval.estimated_cost && (
                        <p className="ep-approval-cost">
                          {lang === 'nl' ? 'Geschatte kosten:' : 'Estimated cost:'} {approval.estimated_cost}
                        </p>
                      )}
                      {approval.status === 'pending' ? (
                        <div className="ep-approval-actions">
                          <button
                            className="ep-approve-btn"
                            onClick={() => respondApproval(approval.id, 'approved')}
                            data-testid={`ep-approve-${approval.id}`}
                          >
                            <Check size={14} />
                            {lang === 'nl' ? 'Goedkeuren' : 'Approve'}
                          </button>
                          <button
                            className="ep-deny-btn"
                            onClick={() => respondApproval(approval.id, 'denied')}
                            data-testid={`ep-deny-${approval.id}`}
                          >
                            <XCircle size={14} />
                            {lang === 'nl' ? 'Afwijzen' : 'Deny'}
                          </button>
                        </div>
                      ) : (
                        <div className={`ep-approval-status ${approval.status}`}>
                          {approval.status === 'approved'
                            ? (lang === 'nl' ? 'Goedgekeurd' : 'Approved')
                            : (lang === 'nl' ? 'Afgewezen' : 'Denied')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── TIMELINE TAB ── */}
          {activeTab === 'timeline' && (
            <div className="ep-section" data-testid="ep-timeline-section">
              {timeline.length === 0 ? (
                <div className="ep-empty" data-testid="ep-no-timeline">
                  <ScrollText size={40} style={{ color: 'var(--text-muted)' }} />
                  <p>{lang === 'nl'
                    ? 'Nog geen activiteit in de tijdlijn — die verschijnt hier zodra je aan de slag gaat.'
                    : 'No activity in the timeline yet — it will appear here as you work.'}</p>
                </div>
              ) : (
                <div className="ep-timeline">
                  {(Array.isArray(visibleTimeline) ? visibleTimeline : []).map((event, i) => {
                    const isEmma = event.actor === 'emma';
                    return (
                      <div key={i} className="ep-timeline-item" data-testid={`ep-timeline-${i}`}>
                        <div className="ep-timeline-dot-wrap">
                          <div className={`ep-timeline-dot ${isEmma ? 'emma' : 'user'}`} />
                          {i < visibleTimeline.length - 1 && <div className="ep-timeline-line" />}
                        </div>
                        <div className="ep-timeline-content">
                          <div className="ep-timeline-meta">
                            <span className={`ep-timeline-actor ${isEmma ? 'emma' : 'user'}`}>
                              {isEmma ? 'Emma' : (lang === 'nl' ? 'Jij' : 'You')}
                            </span>
                            <span className="ep-timeline-type">
                              {(TYPE_LABELS[t] || {})[event.type] || event.type}
                            </span>
                            <span className="ep-timeline-time">{formatTimeAgo(event.created_at)}</span>
                          </div>
                          <p className="ep-timeline-desc">
                            {lang === 'nl' ? event.description_nl : event.description_en}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {timeline.length > 5 && (
                    <button
                      className="ep-timeline-toggle"
                      onClick={() => setTimelineExpanded(!timelineExpanded)}
                      data-testid="ep-timeline-toggle"
                    >
                      {timelineExpanded
                        ? <><ChevronUp size={14} /> {lang === 'nl' ? 'Minder tonen' : 'Show less'}</>
                        : <><ChevronDown size={14} /> {lang === 'nl' ? `Toon alle ${timeline.length} items` : `Show all ${timeline.length} items`}</>
                      }
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmmaProactive;
